// File: my-spa/src/auth/permissions.ts
import { UserRole } from './authContext';

/**
 * Catálogo de permisos del frontend.
 * IMPORTANTE: debe mantenerse sincronizado con el catálogo del backend
 * (backend-spa/src/auth/constants/permissions.ts), que es la fuente de verdad
 * para la autorización real.
 */
export const PERMISSIONS = {
  // Clientes
  CLIENTS_VIEW: 'clients.view',
  CLIENTS_CREATE: 'clients.create',
  CLIENTS_EDIT: 'clients.edit',
  CLIENTS_DELETE: 'clients.delete',

  // Citas
  APPOINTMENTS_VIEW: 'appointments.view',
  APPOINTMENTS_CREATE: 'appointments.create',
  APPOINTMENTS_EDIT: 'appointments.edit',
  APPOINTMENTS_DELETE: 'appointments.delete',

  // Productos y ventas
  PRODUCTS_MANAGE: 'products.manage',
  PRODUCT_SALES_CREATE: 'product_sales.create',
  PRODUCT_SALES_VIEW: 'product_sales.view',

  // Tratamientos
  TREATMENTS_MANAGE: 'treatments.manage',

  // Empleados
  EMPLOYEES_MANAGE: 'employees.manage',
  EMPLOYEE_AVAILABILITIES_MANAGE: 'employee_availabilities.manage',

  // Paquetes
  SERVICES_PACKS_MANAGE: 'services_packs.manage',
  CLIENT_PACKS_MANAGE: 'client_packs.manage',

  // Contenido
  BLOGS_MANAGE: 'blogs.manage',

  // Reportes
  METRICS_VIEW: 'metrics.view',
  PAYROLL_VIEW: 'payroll.view',

  // Comunicación
  WHATSAPP_SEND: 'whatsapp.send',

  // Facturación
  INVOICES_MANAGE: 'invoices.manage',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export interface PermissionOption {
  key: Permission;
  label: string;
}

export interface PermissionGroup {
  module: string;
  label: string;
  permissions: PermissionOption[];
}

/**
 * Agrupación de permisos por módulo para construir el menú de accesos.
 */
export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    module: 'clients',
    label: 'Clientes',
    permissions: [
      { key: PERMISSIONS.CLIENTS_VIEW, label: 'Ver clientes' },
      { key: PERMISSIONS.CLIENTS_CREATE, label: 'Crear clientes' },
      { key: PERMISSIONS.CLIENTS_EDIT, label: 'Editar clientes' },
      { key: PERMISSIONS.CLIENTS_DELETE, label: 'Eliminar clientes' },
    ],
  },
  {
    module: 'appointments',
    label: 'Citas',
    permissions: [
      { key: PERMISSIONS.APPOINTMENTS_VIEW, label: 'Ver citas' },
      { key: PERMISSIONS.APPOINTMENTS_CREATE, label: 'Crear citas' },
      { key: PERMISSIONS.APPOINTMENTS_EDIT, label: 'Editar citas' },
      { key: PERMISSIONS.APPOINTMENTS_DELETE, label: 'Eliminar citas' },
    ],
  },
  {
    module: 'products',
    label: 'Productos y ventas',
    permissions: [
      { key: PERMISSIONS.PRODUCTS_MANAGE, label: 'Gestionar productos' },
      { key: PERMISSIONS.PRODUCT_SALES_CREATE, label: 'Registrar ventas' },
      { key: PERMISSIONS.PRODUCT_SALES_VIEW, label: 'Ver historial de ventas' },
    ],
  },
  {
    module: 'treatments',
    label: 'Tratamientos',
    permissions: [
      { key: PERMISSIONS.TREATMENTS_MANAGE, label: 'Gestionar tratamientos' },
    ],
  },
  {
    module: 'employees',
    label: 'Empleados',
    permissions: [
      { key: PERMISSIONS.EMPLOYEES_MANAGE, label: 'Gestionar empleados' },
      {
        key: PERMISSIONS.EMPLOYEE_AVAILABILITIES_MANAGE,
        label: 'Gestionar disponibilidad',
      },
    ],
  },
  {
    module: 'packs',
    label: 'Paquetes',
    permissions: [
      { key: PERMISSIONS.SERVICES_PACKS_MANAGE, label: 'Gestionar paquetes' },
      {
        key: PERMISSIONS.CLIENT_PACKS_MANAGE,
        label: 'Gestionar paquetes de clientes',
      },
    ],
  },
  {
    module: 'content',
    label: 'Contenido',
    permissions: [{ key: PERMISSIONS.BLOGS_MANAGE, label: 'Gestionar blogs' }],
  },
  {
    module: 'reports',
    label: 'Reportes',
    permissions: [
      { key: PERMISSIONS.METRICS_VIEW, label: 'Ver métricas' },
      { key: PERMISSIONS.PAYROLL_VIEW, label: 'Ver liquidación de empleados' },
    ],
  },
  {
    module: 'comms',
    label: 'Comunicación',
    permissions: [
      { key: PERMISSIONS.WHATSAPP_SEND, label: 'Enviar mensajes de WhatsApp' },
    ],
  },
  {
    module: 'invoices',
    label: 'Facturación',
    permissions: [
      { key: PERMISSIONS.INVOICES_MANAGE, label: 'Gestionar facturas' },
    ],
  },
];

export const ALL_PERMISSIONS: Permission[] = Object.values(PERMISSIONS);

/**
 * Permisos predeterminados por rol (espejo de los del backend).
 */
export const ROLE_DEFAULT_PERMISSIONS: Record<UserRole, Permission[]> = {
  [UserRole.ADMIN]: ALL_PERMISSIONS,
  [UserRole.STAFF]: [
    PERMISSIONS.CLIENTS_VIEW,
    PERMISSIONS.APPOINTMENTS_VIEW,
    PERMISSIONS.APPOINTMENTS_CREATE,
    PERMISSIONS.APPOINTMENTS_EDIT,
    PERMISSIONS.PRODUCT_SALES_CREATE,
    PERMISSIONS.INVOICES_MANAGE,
  ],
};

/**
 * Devuelve los permisos efectivos de un usuario.
 * - ADMIN siempre tiene todos.
 * - Si el usuario tiene permisos explícitos (aunque sea array vacío) se respetan.
 * - Si son null/undefined se usan los predeterminados del rol.
 */
export function getEffectivePermissions(
  role: UserRole,
  permissions?: string[] | null,
): Permission[] {
  if (role === UserRole.ADMIN) {
    return ALL_PERMISSIONS;
  }

  if (permissions != null) {
    return permissions.filter((permission): permission is Permission =>
      (ALL_PERMISSIONS as string[]).includes(permission),
    );
  }

  return ROLE_DEFAULT_PERMISSIONS[role] ?? [];
}
